import React, { useMemo, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import moment from "moment"
import NavigationLink from "~/library/NavigationLink"

import { ResponsiveBar } from "@nivo/bar"
import { nivoTheme } from "~/utils/nivoTheme"

import LoadingWrapper from "~/library/LoadingWrapper"
import Pagination from "~/library/Pagination"
import useFetchResource from "~/hooks/useFetchResource"
import getAppConfig from "~/utils/getAppConfig"
import { apiBookings, apiStatisticsBookings, apiExports } from "~/routes"
import { ExternalLink } from "lucide-react"
import ButtonGroup from "~/library/UIkit/ButtonGroup"
import type Booking from "~/types/Booking"
import { formatCurrencyCents } from "~/utils/helpers"
import CatchTable from "~/library/CatchTable"
import CatchCard from "~/library/CatchCard"
import { Download } from "~/library/UIkit/Icons"
import { useToast } from "~/utils/Toast"

interface BookingReportProps {
  startDate?: string
  endDate?: string
}

type BookingScope = "all" | "upcoming"
type BookingView = "card" | "table"

const BookingReport: React.FC<BookingReportProps> = ({ startDate, endDate }) => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()

  const [bookingView, setBookingView] = useState<string>("card")
  const [bookingScope, setBookingScope] = useState<string>("all")
  const [isExporting, setIsExporting] = useState(false)

  const queryParams = (overrides: Partial<Record<string, string>> = {}) => ({
    scope: bookingScope,
    page: "1",
    ...overrides,
  })

  const {
    data: bookings,
    isLoading,
    refetch,
    isRefetching,
    metadata,
  } = useFetchResource<{ bookings: Booking[] }, "bookings">(
    apiBookings.index.path({ venueSlug }),
    "bookings",
    queryParams({ startDate, endDate })
  )

  const { data: statisticsData, refetch: refetchStats } = useFetchResource<
    { metrics: any },
    "metrics"
  >(apiStatisticsBookings.index.path({ venueSlug }), "metrics", {
    startDate: startDate || "",
    endDate: endDate || "",
    scope: bookingScope,
  })

  const formatDate = (dateString: string) => moment(dateString).format("MMM DD YYYY")

  const getStatusBadge = (status: string) => {
    if (status === "pending") {
      return <span className="badge badge-lg bg-warning">Pending</span>
    } else if (status === "confirmed") {
      return <span className="badge badge-lg bg-success">Confirmed</span>
    }
    return <span className="badge badge-lg bg-secondary">{status}</span>
  }

  const handleBookingViewChange = (value: BookingView) => {
    setBookingView(value)
  }

  const handleScopeChange = (value: BookingScope) => {
    setBookingScope(value)

    if (!startDate || !endDate) return

    refetch(
      queryParams({
        scope: value,
        page: "1",
        startDate: startDate,
        endDate: endDate,
      })
    )

    refetchStats({
      startDate: startDate,
      endDate: endDate,
      scope: value,
    })
  }

  const handlePageChange = (page: string) => {
    if (!startDate || !endDate) return

    refetch(
      queryParams({
        page,
        startDate: startDate,
        endDate: endDate,
      })
    )
  }

  const handleExportCsv = async () => {
    if (bookings?.length === 0) return

    if (!startDate || !endDate) {
      toast("Start date and end date are required for export", "danger")
      return
    }

    setIsExporting(true)

    try {
      const exportUrl = apiExports.index.path({ venueSlug })
      const params = new URLSearchParams({
        method: "bookings",
        start_date: startDate,
        end_date: endDate,
        scope: bookingScope,
      })

      const response = await fetch(`${exportUrl}?${params.toString()}`, {
        method: "GET",
        headers: {
          Accept: "text/csv",
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.errors?.[0] || `HTTP error! status: ${response.status}`)
      }

      const csvContent = await response.text()
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = `catch-bookings-${moment().format("YYYY-MM-DD-HHmmss")}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      toast(
        `Failed to export CSV: ${error instanceof Error ? error.message : "Unknown error"}`,
        "danger"
      )
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    if (!startDate || !endDate) return

    refetch(
      queryParams({
        page: "1",
        startDate: startDate,
        endDate: endDate,
      })
    )

    refetchStats({
      startDate: startDate,
      endDate: endDate,
      scope: bookingScope,
    })
  }, [startDate, endDate, bookingScope])

  const bookingCards = useMemo(() => {
    if (!Array.isArray(bookings)) return null

    return bookings.map((booking: any) => (
      <div key={booking.id} className="col col-6 mb-3">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <NavigationLink
                  type="bookings"
                  to={`/bookings/${booking.id}`}
                  className="text-decoration-none"
                >
                  <h3 className="mb-0">{booking.name}</h3>
                </NavigationLink>
              </div>
              <div className="d-flex gap-2">
                {getStatusBadge(booking.status)}
                <NavigationLink type="bookings" to={`/bookings/${booking.id}`}>
                  <ExternalLink size={24} />
                </NavigationLink>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-3">
                <h5 className="text-muted mb-1">Area</h5>
                <h6 className="mb-0">{booking.area?.name || "N/A"}</h6>
              </div>
              <div className="col-3">
                <h5 className="text-muted mb-1">Date</h5>
                <h6 className="mb-0">{formatDate(booking.date)}</h6>
              </div>
              <div className="col-3">
                <h5 className="text-muted mb-1">Client</h5>
                {booking.client ? (
                  <NavigationLink
                    type="clients"
                    to={`/clients/${booking.client.id}`}
                    className="text-decoration-none"
                  >
                    <div className="d-flex align-items-center">
                      <img
                        src={`https://avatar.iran.liara.run/username?username=${booking.client.name
                          .split(" ")
                          .join("+")}`}
                        className="rounded-circle avatar-xs me-2"
                        alt={booking.client.name}
                      />
                      <h6 className="mb-0 mt-0">{booking.client.name}</h6>
                    </div>
                  </NavigationLink>
                ) : (
                  <h6 className="mb-0 text-muted">No client</h6>
                )}
              </div>
              <div className="col-3">
                <h5 className="text-muted mb-1">Sales Rep</h5>
                {booking.createdBy ? (
                  <div className="d-flex align-items-center">
                    <img
                      src={`https://avatar.iran.liara.run/username?username=${booking.createdBy.name
                        .split(" ")
                        .join("+")}`}
                      className="rounded-circle avatar-xs me-2"
                      alt={booking.createdBy.name}
                    />
                    <h6 className="mb-0 mt-0">{booking.createdBy.name}</h6>
                  </div>
                ) : (
                  <h6 className="mb-0 text-muted">N/A</h6>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <h5 className="text-muted mb-1">Total Revenue</h5>
                <h4 className="mb-0 text-success">
                  {formatCurrencyCents(booking.totalAmountCents || 0)}
                </h4>
              </div>
              <div className="col-6">
                <h5 className="text-muted mb-1">Total Payments</h5>
                <h4 className="mb-0 text-info">
                  {formatCurrencyCents(booking.paymentsAmountCents || 0)}
                </h4>
              </div>
            </div>

            {booking.details && (
              <div className="mb-3">
                <label className="form-label text-muted">Details</label>
                <p className="mb-0">{booking.details}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    ))
  }, [bookings])

  return (
    <>
      <div className="d-flex justify-content-between mb-2">
        <ButtonGroup
          options={[
            { label: "Card View", value: "card" },
            { label: "Table View", value: "table" },
          ]}
          selectedValue={bookingView}
          onClick={(value) => handleBookingViewChange(value as BookingView)}
        />
        <div className="d-flex align-items-center gap-2">
          <ButtonGroup
            options={[
              { label: "All", value: "all" },
              { label: "Upcoming", value: "upcoming" },
            ]}
            selectedValue={bookingScope}
            onClick={(value) => handleScopeChange(value as BookingScope)}
          />
          <button
            className="btn-catch-secondary btn btn-secondary"
            onClick={() => {
              handleExportCsv()
            }}
            title="Export CSV"
            disabled={isExporting || !startDate || !endDate || bookings?.length === 0}
          >
            {isExporting ? (
              <div className="spinner-border spinner-border-sm text-white" role="status"></div>
            ) : (
              <Download color={"#ffffff"} />
            )}
          </button>
        </div>
      </div>

      {statisticsData && (
        <div className="row mb-3">
          <div className="col-3">
            <div className="card text-center">
              <div className="card-body">
                <h5>Total Bookings</h5>
                <h3>{statisticsData.total_bookings || 0}</h3>
              </div>
            </div>
            <div className="card text-center">
              <div className="card-body">
                <h5>Average Booking Revenue</h5>
                <h3>{formatCurrencyCents(statisticsData.average_booking_revenue_cents || 0)}</h3>
              </div>
            </div>
          </div>
          <div className="col-3">
            <div className="card text-center">
              <div className="card-body">
                <h5>Total Revenue</h5>
                <h3>{formatCurrencyCents(statisticsData.total_revenue_cents || 0)}</h3>
              </div>
            </div>
            <div className="card text-center">
              <div className="card-body">
                <h5>Paid Revenue</h5>
                <h3>{formatCurrencyCents(statisticsData.paid_revenue_cents || 0)}</h3>
              </div>
            </div>
            <div className="card text-center">
              <div className="card-body">
                <h5>Pending Revenue</h5>
                <h3>{formatCurrencyCents(statisticsData.pending_revenue_cents || 0)}</h3>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="card">
              <div className="card-body">
                <h5>Top 3 Booked Areas</h5>
                <div style={{ height: 326, width: "100%" }}>
                  {statisticsData?.top_booked_areas?.length > 0 ? (
                    <ResponsiveBar
                      data={statisticsData?.top_booked_areas}
                      keys={["bookings"]}
                      indexBy="name"
                      layout="vertical"
                      margin={{ top: 10, right: 10, bottom: 40, left: 10 }}
                      padding={0.3}
                      valueScale={{ type: "linear" }}
                      indexScale={{ type: "band", round: true }}
                      colors={{ scheme: "set3" }}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: "middle",
                        legendOffset: 32,
                        truncateTickAt: 16,
                      }}
                      axisLeft={null}
                      enableLabel={true}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      theme={nivoTheme}
                    />
                  ) : (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <h5 className="text-muted">No data available for the selected time period</h5>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <LoadingWrapper isLoading={isLoading || isRefetching}>
        {bookingView === "card" && (
          <div className="row mt-2">
            {Array.isArray(bookings) && bookings.length > 0 ? (
              <>
                {bookingCards}
                {metadata && (
                  <div className="col-12">
                    <div className="d-flex justify-content-center mt-4">
                      <Pagination
                        metadata={metadata}
                        onPageChange={(page: number) => handlePageChange(page.toString())}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center py-5">
                    <h5 className="text-muted">No bookings found for the selected time period</h5>
                    <p className="text-muted">
                      Try adjusting your date range or{" "}
                      <Link to="/bookings/new" className="text-primary">
                        create a new booking
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {bookingView === "table" && (
          <div className="row mt-2">
            <div className="col-12">
              {Array.isArray(bookings) && bookings.length > 0 ? (
                <CatchCard className={"card-dark-theme"}>
                  <CatchTable
                    data={bookings}
                    emptyMessage="No Bookings"
                    columns={[
                      {
                        key: "name",
                        header: "Booking",
                        formatter: (name, data) => (
                          <NavigationLink type="bookings" to={`/bookings/${data.id}`}>
                            {name}
                          </NavigationLink>
                        ),
                      },
                      {
                        key: "area",
                        header: "Area",
                        formatter: (area) =>
                          area ? (
                            <NavigationLink type="areas" to={`/areas/${area.id}`}>
                              {area.name}
                            </NavigationLink>
                          ) : (
                            "N/A"
                          ),
                      },
                      {
                        key: "client",
                        header: "Client",
                        formatter: (client) => (
                          <NavigationLink type="clients" to={`/clients/${client.id}`}>
                            {client.name}
                          </NavigationLink>
                        ),
                      },
                      {
                        key: "date",
                        header: "Date",
                        formatter: (date) => formatDate(date),
                      },
                      {
                        key: "createdBy",
                        header: "Sales Rep",
                        formatter: (createdBy) => (createdBy ? createdBy.name : "N/A"),
                      },
                      {
                        key: "totalAmountCents",
                        header: "Total Revenue",
                        formatter: (totalAmount) => formatCurrencyCents(totalAmount || 0),
                      },
                      {
                        key: "paymentsAmountCents",
                        header: "Total Payments",
                        formatter: (paymentsAmount) => formatCurrencyCents(paymentsAmount),
                      },
                      {
                        key: "status",
                        header: "Status",
                        formatter: (status) => getStatusBadge(status),
                      },
                    ]}
                  />
                  {metadata && (
                    <Pagination
                      metadata={metadata}
                      onPageChange={(page: number) => handlePageChange(page.toString())}
                    />
                  )}
                </CatchCard>
              ) : (
                <div className="card">
                  <div className="card-body text-center py-5">
                    <h5 className="text-muted">No bookings found for the selected time period</h5>
                    <p className="text-muted">
                      Try adjusting your date range or{" "}
                      <Link to="/bookings/new" className="text-primary">
                        create a new booking
                      </Link>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </LoadingWrapper>
    </>
  )
}

export default BookingReport
